import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../utils/auth";
import { useQuery } from "@tanstack/react-query";
import {
  fetchNewOrdersSummary,
  fetchHoldOrdersSummary,
  fetchOldOrdersSummary,
  fetchChangesOrdersSummary,
  fetchDepositRequiredSummary,
  fetchCustomOrdersSummary,
  fetchConfirmedOrdersSummary,
  fetchCanceledOrdersSummary,
  fetchGirabatcomOrdersSummary,
  fetchPrintingHoldOrdersSummary,
  fetchIncompleteOrdersSummary,
  fetchPrintedOrdersSummary,
  fetchPrintletWorkflowOrdersSummary,
  fetchTeePlusOrdersSummary,
} from "../api/OrderAPIs";
import { fetchPrintletDashboard } from "../api/PrintletAPIs";
import { fetchReturns } from "../api/ReturnsAPIs";
import { fetchTicketStatusCounts } from "../api/TicketAPIs";
import { fetchReplacementOrdersSummary } from "../api/ReplacementAPIs";
import {
  ChevronUp,
  ChevronDown,
  ShoppingCart,
  CheckCircle,
  Clock,
  XCircle,
  PackageCheck,
  Archive,
  AlertTriangle,
  Layers,
  Tag,
  RotateCcw,
  Search,
  PackageSearch,
  UserCircle,
  LogOut,
  Shield,
  UserCog,
  Phone,
  MessageSquare,
  Printer,
  Activity,
  BarChart3,
  Truck,
  CreditCard,
  Eye,
  Users,
  RefreshCw,
  Palette,
  ShoppingBag,
  Shapes,
  Smartphone,
  Sparkles,
  TrendingUp,
  Type,
  Ticket,
  Crown,
  ArrowUpRight,
  Zap,
  ScrollText,
} from "lucide-react";
import { images } from "../constants/images";
import NotificationBell from "../Components/notifications/NotificationBell";
import {
  canAccessPanel,
  canViewOrders,
  canViewOrderActivity,
  canViewReturns,
  canViewTickets,
  canViewFinancials,
  canViewAnalytics,
  canViewCustomerAnalytics,
  isOwner,
  isAdmin,
  isViewer,
  getRoleLabel,
  ROLES,
} from "../utils/roles";

const DASHBOARD_TAB_KEY = "adminDashboardTab";

const getStoredDashboardTab = () => {
  try {
    const saved = sessionStorage.getItem(DASHBOARD_TAB_KEY);
    if (saved === "printlet") return "production";
    if (saved === "orders" || saved === "printing" || saved === "production") return saved;
  } catch (_) {}
  return "orders";
};

// ─────────────────────────────────────────────────────────────
// Reusable UI atoms
// ─────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="animate-pulse bg-white/10 rounded-xl p-3 border border-white/10">
    <div className="h-5 bg-white/20 rounded w-3/5 mb-2" />
    <div className="h-4 bg-white/15 rounded w-4/5" />
  </div>
);

function RoleBadge({ role, size = "md" }) {
  const roleStyle = {
    [ROLES.OWNER]:     { icon: Crown,   color: "from-amber-500 to-orange-500",    bg: "from-amber-50 to-orange-50 border-amber-200 text-amber-800" },
    [ROLES.ADMIN]:     { icon: Shield,  color: "from-purple-500 to-fuchsia-500", bg: "from-purple-50 to-fuchsia-50 border-purple-200 text-purple-700" },
    [ROLES.MODERATOR]: { icon: UserCog, color: "from-blue-500 to-cyan-500",      bg: "from-blue-50 to-cyan-50 border-blue-200 text-blue-700" },
    [ROLES.VIEWER]:    { icon: Eye,     color: "from-slate-500 to-slate-600",    bg: "from-slate-50 to-slate-100 border-slate-200 text-slate-700" },
  }[role] || { icon: UserCircle, color: "from-slate-400 to-slate-500", bg: "from-slate-50 to-slate-100 border-slate-200 text-slate-700" };

  const Icon = roleStyle.icon;
  const isSmall = size === "sm";

  return (
    <div
      className={`inline-flex items-center gap-2 bg-gradient-to-r ${roleStyle.bg} border rounded-xl ${
        isSmall ? "px-2 py-1" : "px-3.5 py-2"
      }`}
    >
      <Icon size={isSmall ? 13 : 16} />
      <span className={`font-bold ${isSmall ? "text-xs" : "text-sm"}`}>
        {getRoleLabel(role, { short: isSmall })}
      </span>
    </div>
  );
}

function StatTile({ label, value, hint, accent = "indigo", icon: Icon }) {
  const accents = {
    indigo:  { bg: "from-indigo-500/20 to-purple-500/10", text: "text-indigo-600",  ring: "ring-indigo-400/30" },
    emerald: { bg: "from-emerald-500/20 to-teal-500/10",  text: "text-emerald-600", ring: "ring-emerald-400/30" },
    amber:   { bg: "from-amber-500/20 to-orange-500/10",  text: "text-amber-600",   ring: "ring-amber-400/30" },
    rose:    { bg: "from-rose-500/20 to-pink-500/10",     text: "text-rose-600",    ring: "ring-rose-400/30" },
  };
  const a = accents[accent] || accents.indigo;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${a.bg} ring-1 ${a.ring} backdrop-blur-md p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-600 mb-1">{label}</p>
          <p className="text-3xl font-extrabold text-slate-800 leading-none">{value}</p>
          {hint && <p className="text-xs text-slate-500 mt-1.5">{hint}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-xl bg-white shadow-sm ${a.text}`}>
            <Icon size={18} />
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ item, onClick, options = {} }) {
  const { showAmount = true, showPieces = false, compact = false } = options;
  const showPieceCount = showPieces || item.showPieces;
  const isLoading = item.loading;

  return (
    <button
      onClick={onClick}
      className={`
        group relative overflow-hidden text-white text-right
        bg-gradient-to-br ${item.color}
        rounded-xl ${compact ? "p-3" : "p-3.5"}
        shadow-md shadow-black/5
        hover:shadow-xl hover:shadow-black/15 hover:scale-[1.02]
        active:scale-[0.985]
        transition-all duration-300
        border border-white/20
        flex flex-col justify-between
        ${item.prominent ? "min-h-[148px] md:min-h-[168px] items-center text-center" : "min-h-[96px] md:min-h-[108px]"}
        ${item.highlight && (item.data?.count ?? 0) > 0 ? "ring-4 ring-white/80 shadow-lg shadow-rose-900/25" : ""}
      `}
    >
      <div className="pointer-events-none absolute -top-6 -left-6 w-20 h-20 rounded-full bg-white/10 blur-xl group-hover:scale-150 transition-transform duration-700" />
      <div className="pointer-events-none absolute -bottom-8 -right-8 w-24 h-24 rounded-full bg-white/5 blur-2xl" />

      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 translate-x-2 transition-all duration-300">
        <div className="p-1 rounded-full bg-white/20 backdrop-blur-sm">
          <ArrowUpRight size={12} />
        </div>
      </div>

      <h3 className={`relative font-bold leading-snug ${item.prominent ? "text-sm md:text-base tracking-wide" : "text-sm md:text-lg"}`}>
        {item.title}
      </h3>

      {isLoading ? (
        <SkeletonCard />
      ) : item.hideCount ? (
        <p className="relative text-sm opacity-90 py-1">{item.subtitle || "فتح الصفحة"}</p>
      ) : item.prominent ? (
        <div className="relative mt-1 flex flex-col items-center">
          <p className="text-5xl md:text-6xl font-black leading-none tracking-tight">
            {item.data?.count ?? 0}
          </p>
          <p className="mt-2 text-sm md:text-base font-semibold opacity-95">
            {item.subtitle || "تحتاج متابعة"}
          </p>
        </div>
      ) : (
        <div className="relative mt-2">
          <div className="flex items-end gap-1.5">
            <p className="text-xl md:text-3xl font-extrabold leading-none tracking-tight">
              {item.data?.count ?? 0}
            </p>
            <span className="text-sm opacity-90 pb-0.5">{item.unit || "طلب"}</span>
          </div>

          {showPieceCount && (
            <div className="pt-2 mt-2 border-t border-white/20">
              <p className="text-xs opacity-80">عدد القطع</p>
              <p className="text-base font-bold">{(item.data?.totalProductsSold ?? 0).toLocaleString()}</p>
            </div>
          )}

          {item.extraLabel && !item.hideSecondaryStats && (
            <div className="pt-2 mt-2 border-t border-white/20">
              <p className="text-xs opacity-80">{item.extraLabel}</p>
              <p className="text-base font-bold">
                {(item.extraValue ?? item.data?.extraValue ?? 0).toLocaleString()}
              </p>
            </div>
          )}

          {showAmount && !item.extraLabel && !item.hideSecondaryStats && (
            <div className="pt-2 mt-2 border-t border-white/20">
              <p className="text-xs opacity-80">إجمالي المبلغ</p>
              <p className="text-base font-bold">
                {(item.data?.totalAmount ?? 0).toLocaleString()} <span className="text-xs opacity-80">ج.م</span>
              </p>
            </div>
          )}
        </div>
      )}
    </button>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color = "indigo", isOpen, onToggle }) {
  const colors = {
    indigo:  "from-indigo-600 to-purple-600 text-indigo-700",
    amber:   "from-orange-600 to-amber-600 text-orange-700",
    emerald: "from-emerald-600 to-teal-600 text-emerald-700",
    blue:    "from-blue-600 to-cyan-600 text-blue-700",
    slate:   "from-slate-700 to-slate-900 text-slate-800",
    rose:    "from-rose-600 to-pink-600 text-rose-700",
  };
  const c = colors[color] || colors.indigo;

  return (
    <button
      onClick={onToggle}
      className="group w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-2xl px-3.5 py-3.5 md:px-5 md:py-4 hover:border-slate-300 hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${c} text-white shadow-sm`}>
          <Icon size={18} />
        </div>
        <div className="text-right">
          <h3 className={`text-base md:text-lg font-bold bg-gradient-to-r ${c} bg-clip-text text-transparent`}>
            {title}
          </h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="p-2 rounded-lg bg-slate-50 group-hover:bg-slate-100 transition">
        {isOpen ? <ChevronUp size={18} className="text-slate-500" /> : <ChevronDown size={18} className="text-slate-500" />}
      </div>
    </button>
  );
}

function ActionCard({ icon: Icon, title, description, onClick, accent = "indigo", span = 1 }) {
  const accents = {
    indigo:  "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100",
    emerald: "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100",
    amber:   "bg-amber-50 text-amber-600 group-hover:bg-amber-100",
    rose:    "bg-rose-50 text-rose-600 group-hover:bg-rose-100",
    blue:    "bg-blue-50 text-blue-600 group-hover:bg-blue-100",
    purple:  "bg-purple-50 text-purple-600 group-hover:bg-purple-100",
    teal:    "bg-teal-50 text-teal-600 group-hover:bg-teal-100",
    pink:    "bg-pink-50 text-pink-600 group-hover:bg-pink-100",
    slate:   "bg-slate-100 text-slate-600 group-hover:bg-slate-200",
    orange:  "bg-orange-50 text-orange-600 group-hover:bg-orange-100",
    violet:  "bg-violet-50 text-violet-600 group-hover:bg-violet-100",
    fuchsia: "bg-fuchsia-50 text-fuchsia-600 group-hover:bg-fuchsia-100",
  };
  const a = accents[accent] || accents.indigo;

  return (
    <button
      onClick={onClick}
      className={`
        group bg-white border border-slate-200 rounded-2xl p-4 md:p-5
        hover:border-slate-300 hover:shadow-lg hover:-translate-y-0.5
        transition-all duration-300 flex items-center gap-3 md:gap-4 text-right min-w-0
        ${span === 2 ? "md:col-span-2" : ""}
      `}
    >
      <div className={`p-3.5 rounded-xl transition shrink-0 ${a}`}>
        <Icon size={24} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-base md:text-lg font-bold text-slate-800 truncate">{title}</p>
        <p className="text-sm text-slate-500 mt-0.5 leading-snug">{description}</p>
      </div>
      <ArrowUpRight size={18} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOthersOpen, setIsOthersOpen] = useState(false);
  const [isExecuteOpen, setIsExecuteOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [dashboardTab, setDashboardTabState] = useState(getStoredDashboardTab);

  const setDashboardTab = (tab) => {
    setDashboardTabState(tab);
    try { sessionStorage.setItem(DASHBOARD_TAB_KEY, tab); } catch (_) {}
  };

  // ── Queries ────────────────────────────────────────
  const commonOpts = (enabled = true) => ({ enabled, staleTime: Infinity });

  const { data: newOrdersSummary, isLoading: isLoadingNew } = useQuery({
    queryKey: ["newOrdersSummary"], queryFn: fetchNewOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: holdOrdersSummary, isLoading: isLoadingHold } = useQuery({
    queryKey: ["holdOrdersSummary"], queryFn: fetchHoldOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: changesOrdersSummary, isLoading: isLoadingChanges } = useQuery({
    queryKey: ["changesOrdersSummary"], queryFn: fetchChangesOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: depositRequiredSummary, isLoading: isLoadingDeposit } = useQuery({
    queryKey: ["depositRequiredSummary"], queryFn: fetchDepositRequiredSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: customOrdersSummary, isLoading: isLoadingCustom } = useQuery({
    queryKey: ["customOrdersSummary"], queryFn: fetchCustomOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: canceledOrdersSummary, isLoading: isLoadingCanceled } = useQuery({
    queryKey: ["canceledOrdersSummary"], queryFn: fetchCanceledOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: confirmedOrdersSummary, isLoading: isLoadingConfirmed } = useQuery({
    queryKey: ["confirmedOrdersSummary"], queryFn: fetchConfirmedOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0 },
  });
  const { data: girabatcomOrdersSummary, isLoading: isLoadingGirabatcom } = useQuery({
    queryKey: ["girabatcomOrdersSummary"], queryFn: fetchGirabatcomOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const { data: printingHoldOrdersSummary, isLoading: isLoadingPrintingHold } = useQuery({
    queryKey: ["printingHoldOrdersSummary"], queryFn: fetchPrintingHoldOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const { data: incompleteOrdersSummary, isLoading: isLoadingIncomplete } = useQuery({
    queryKey: ["incompleteOrdersSummary"], queryFn: fetchIncompleteOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const { data: printedOrdersSummary, isLoading: isLoadingPrinted } = useQuery({
    queryKey: ["printedOrdersSummary"], queryFn: fetchPrintedOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProductsSold: 0 },
  });
  const { data: printletWorkflowSummary, isLoading: isLoadingPrintletWorkflow } = useQuery({
    queryKey: ["printletWorkflowOrdersSummary"], queryFn: fetchPrintletWorkflowOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProfit: 0 },
  });
  const { data: teePlusOrdersSummary, isLoading: isLoadingTeePlus } = useQuery({
    queryKey: ["teePlusOrdersSummary"], queryFn: fetchTeePlusOrdersSummary,
    ...commonOpts(canViewOrders(authState.role)),
    placeholderData: { count: 0, totalAmount: 0, totalProfit: 0 },
  });
  const { data: ticketStatusCounts, isLoading: isLoadingTickets } = useQuery({
    queryKey: ["ticketStatusCounts"], queryFn: fetchTicketStatusCounts,
    ...commonOpts(canViewTickets(authState.role)),
    placeholderData: { all: 0, open: 0, inProgress: 0, waitingForCustomer: 0, active: 0 },
  });
  const { data: replacementOrdersSummary, isLoading: isLoadingReplacements } = useQuery({
    queryKey: ["replacementOrdersSummary"], queryFn: fetchReplacementOrdersSummary,
    ...commonOpts(canViewTickets(authState.role)),
    placeholderData: { count: 0, totalProductsSold: 0 },
  });
  const { data: returnsSummary, isLoading: isLoadingReturns } = useQuery({
    queryKey: ["returnsTotalCount"],
    queryFn: () => fetchReturns({ page: 0, pageSize: 1, status: "All" }),
    ...commonOpts(canViewReturns(authState.role)),
    placeholderData: { totalCount: 0 },
  });
  const { data: printletDashboard, isLoading: isLoadingPrintletDash } = useQuery({
    queryKey: ["printletDashboardHome"],
    queryFn: () => fetchPrintletDashboard(),
    ...commonOpts(canViewOrders(authState.role) && isAdmin(authState.role)),
  });

  const ticketCount = (key) =>
    Number(
      ticketStatusCounts?.[key] ??
        ticketStatusCounts?.[key.charAt(0).toUpperCase() + key.slice(1)] ??
        0
    );

  const unresolvedTickets =
    ticketCount("active") ||
    ticketCount("open") + ticketCount("inProgress") + ticketCount("waitingForCustomer");

  const ticketsCard = {
    title: "تذاكر مفتوحة",
    data: { count: unresolvedTickets },
    color: "from-rose-600 to-pink-500",
    path: "/tickets",
    subtitle: "تحتاج متابعة",
    prominent: true,
    highlight: true,
    requiresTickets: true,
    loading: isLoadingTickets,
  };

  const returnsCard = {
    title: "المرتجعات",
    data: { count: returnsSummary?.totalCount ?? 0 },
    color: "from-sky-700 to-cyan-500",
    path: "/returns",
    hideSecondaryStats: true,
    subtitle: "مخزون القطع للإنتاج",
    prominent: true,
    requiresReturns: true,
    loading: isLoadingReturns,
  };

  const isLoading =
    isLoadingNew || isLoadingHold || isLoadingChanges || isLoadingDeposit ||
    isLoadingCustom || isLoadingPrintingHold || isLoadingIncomplete || isLoadingPrinted ||
    isLoadingGirabatcom || isLoadingPrintletWorkflow || isLoadingTeePlus;

  useEffect(() => {
    if (!canAccessPanel(authState.role)) navigate("/login");
  }, [authState, navigate]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const calculateGrandTotals = () => {
    const summaries = [confirmedOrdersSummary, changesOrdersSummary, depositRequiredSummary, customOrdersSummary];
    let totalOrders = 0;
    let totalAmount = 0;
    summaries.forEach((s) => {
      if (s) {
        totalOrders += Number(s.count || 0);
        totalAmount += Number(s.totalAmount || 0);
      }
    });
    return { totalOrders, totalAmount: Math.round(totalAmount) };
  };

  const { totalOrders, totalAmount } = calculateGrandTotals();

  const replacementCard = {
    title: "طلبات الاستبدال",
    color: "from-violet-600 to-indigo-500",
    path: "/replacement-orders",
    data: {
      count: Number(replacementOrdersSummary?.count ?? replacementOrdersSummary?.Count ?? 0),
      totalProductsSold: Number(
        replacementOrdersSummary?.totalProductsSold ?? replacementOrdersSummary?.TotalProductsSold ?? 0
      ),
    },
    showPieces: true,
    hideSecondaryStats: true,
    requiresTickets: true,
    loading: isLoadingReplacements,
  };

  const orderCardsAdmin = [
    { title: "الطلبات الجديدة",     data: newOrdersSummary,          color: "from-blue-600 to-cyan-500",       path: "/new-orders" },
    { title: "الطلبات المؤكدة",     data: confirmedOrdersSummary,    color: "from-emerald-600 to-green-500",   path: "/confirmed-orders" },
    { title: "الطلبات الملغاة",     data: canceledOrdersSummary,     color: "from-red-600 to-rose-500",        path: "/canceled-orders" },
    { title: "التصاميم المخصصة",    data: customOrdersSummary,       color: "from-teal-600 to-emerald-500",    path: "/custom-orders" },
    { title: "الطلبات المعلقة",     data: holdOrdersSummary,         color: "from-amber-500 to-yellow-500",    path: "/hold-orders" },
    { title: "تحت التغييرات",       data: changesOrdersSummary,      color: "from-purple-600 to-violet-500",   path: "/changes-orders" },
    { title: "تتطلب دفعة أولية",    data: depositRequiredSummary,    color: "from-orange-500 to-amber-500",    path: "/deposit-required-orders" },
    ticketsCard,
    replacementCard,
  ];

  const orderCardsStaff = [
    { title: "الطلبات الجديدة",     data: newOrdersSummary,          color: "from-blue-500 to-cyan-500",       path: "/new-orders" },
    { title: "الطلبات المؤكدة",     data: confirmedOrdersSummary,    color: "from-emerald-500 to-teal-500",    path: "/confirmed-orders" },
    { title: "الطلبات الملغاة",     data: canceledOrdersSummary,     color: "from-red-600 to-rose-500",        path: "/canceled-orders" },
    { title: "الطلبات المعلقة",     data: holdOrdersSummary,         color: "from-amber-500 to-yellow-500",    path: "/hold-orders" },
    { title: "تتطلب دفعة أولية",    data: depositRequiredSummary,    color: "from-orange-500 to-amber-500",    path: "/deposit-required-orders" },
    { title: "تحت التغييرات",       data: changesOrdersSummary,      color: "from-purple-600 to-violet-500",   path: "/changes-orders" },
    { title: "التصاميم المخصصة",    data: customOrdersSummary,       color: "from-teal-500 to-emerald-500",    path: "/custom-orders" },
    ticketsCard,
    replacementCard,
  ];

  const printingCards = [
    { title: "Printed",         data: printedOrdersSummary,        color: "from-teal-600 to-emerald-500",   path: "/printed-orders" },
    { title: "Girabatcom",      data: girabatcomOrdersSummary,     color: "from-cyan-600 to-blue-500",      path: "/girabatcom-orders" },
    { title: "Printing Hold",   data: printingHoldOrdersSummary,   color: "from-slate-700 to-cyan-500",     path: "/printing-hold-orders" },
    { title: "Incomplete",      data: incompleteOrdersSummary,     color: "from-amber-600 to-orange-500",   path: "/incomplete-orders" },
    ticketsCard,
    replacementCard,
    returnsCard,
  ];

  const printletCards = [
    { title: "طلبات Printlet", data: printletWorkflowSummary, color: "from-fuchsia-600 to-pink-500", path: "/printlet-orders" },
    { title: "Printing Hold", data: printingHoldOrdersSummary, color: "from-slate-700 to-cyan-500", path: "/printing-hold-orders" },
    { title: "Tee Plus", data: teePlusOrdersSummary, color: "from-emerald-600 to-teal-500", path: "/teeplus-orders" },
    ticketsCard,
  ];

  const printletStats = [
    { label: "أُرسلت", value: printletDashboard?.totalSent ?? 0 },
    { label: "قيد الإنتاج", value: printletDashboard?.inProduction ?? 0 },
    { label: "شُحنت", value: printletDashboard?.shipped ?? 0 },
    { label: "سُلّمت", value: printletDashboard?.delivered ?? 0 },
    { label: "مرتجعة", value: printletDashboard?.returned ?? 0 },
    { label: "فشل", value: printletDashboard?.failed ?? 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Subtle grid background */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(99,102,241,0.10), transparent 40%), radial-gradient(circle at 80% 30%, rgba(217,70,239,0.08), transparent 45%), radial-gradient(circle at 50% 90%, rgba(20,184,166,0.07), transparent 40%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8 space-y-5 md:space-y-8 overflow-x-hidden">

        {/* ═══════════ HEADER ═══════════ */}
        <header className="sticky top-3 z-40">
          <div className="bg-white/85 backdrop-blur-xl shadow-sm shadow-slate-200/50 rounded-2xl border border-white/60 px-3 py-2.5 md:px-6 md:py-4 flex items-center justify-between gap-2 min-w-0">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              <div className="relative">
                <img src={images.logo} alt="Logo" className="h-9 md:h-10 object-contain" />
              </div>
              <div className="hidden md:block h-8 w-px bg-slate-200" />
              <div className="hidden md:block">
                <RoleBadge role={authState.role} />
              </div>
            </div>

            <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
              <NotificationBell />
              <div className="md:hidden max-w-[140px] overflow-hidden">
                <RoleBadge role={authState.role} size="sm" />
              </div>

              <div className="relative" style={{ zIndex: 9999 }}>
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="flex items-center gap-2 p-1 rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm shadow-indigo-500/30 ring-2 ring-white">
                    <UserCircle size={24} className="text-white" />
                  </div>
                </button>

                {isMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0"
                      style={{ zIndex: 9998 }}
                      onClick={() => setIsMenuOpen(false)}
                    />
                    <div
                      className="absolute left-0 top-14 mt-2 w-64 bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl shadow-slate-300/50 border border-slate-200/60 py-2 overflow-hidden"
                      style={{ zIndex: 9999 }}
                    >
                      <div className="px-5 py-3 border-b border-slate-100">
                        <p className="text-xs text-slate-500 mb-1.5">مسجل الدخول باسم</p>
                        <div className="flex items-center gap-2">
                          <RoleBadge role={authState.role} size="sm" />
                        </div>
                      </div>
                      <button
                        onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={17} />
                        <span className="font-medium text-sm">تسجيل الخروج</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* ═══════════ HERO — Grand totals ═══════════ */}
        {isAdmin(authState.role) && canViewOrders(authState.role) && (
          <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 px-4 py-3.5 md:px-6 md:py-4 shadow-lg shadow-indigo-500/20 border border-white/20">
            <div className="pointer-events-none absolute -top-16 -left-16 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-fuchsia-400/20 blur-3xl" />

            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 text-white">
                  <Sparkles size={16} />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-extrabold text-white leading-tight">إجمالي الطلبات الحالية</h2>
                  <p className="text-xs text-white/70">
                    المؤكدة + التغييرات + الدفعات + المخصصة
                  </p>
                </div>
              </div>

              {isLoading ? (
                <div className={`grid ${canViewFinancials(authState.role) ? "grid-cols-2" : "grid-cols-1"} gap-3 w-full md:w-auto md:min-w-[320px]`}>
                  <div className="bg-white/15 rounded-xl animate-pulse h-14" />
                  {canViewFinancials(authState.role) && (
                    <div className="bg-white/15 rounded-xl animate-pulse h-14" />
                  )}
                </div>
              ) : (
                <div className={`grid ${canViewFinancials(authState.role) ? "grid-cols-2" : "grid-cols-1"} gap-3 w-full md:w-auto md:min-w-[320px]`}>
                  <div className="bg-white/15 backdrop-blur-lg rounded-xl px-3 py-2 border border-white/20">
                    <div className="flex items-center gap-1.5 text-white/70 text-[11px] mb-0.5">
                      <ShoppingCart size={12} />
                      عدد الطلبات
                    </div>
                    <p className="text-xl md:text-2xl font-extrabold text-white leading-none">
                      {totalOrders.toLocaleString()}
                    </p>
                  </div>
                  {canViewFinancials(authState.role) && (
                    <div className="bg-white/15 backdrop-blur-lg rounded-xl px-3 py-2 border border-white/20">
                      <div className="flex items-center gap-1.5 text-white/70 text-[11px] mb-0.5">
                        <TrendingUp size={12} />
                        إجمالي المبلغ
                      </div>
                      <p className="text-xl md:text-2xl font-extrabold text-white leading-none">
                        {totalAmount.toLocaleString()}
                      </p>
                      <p className="text-white/60 text-[10px] mt-0.5">جنيه مصري</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* ═══════════ TABS ═══════════ */}
        {canViewOrders(authState.role) && (
          <div className="space-y-6">
            {/* Tab switcher */}
            <div className="bg-white rounded-2xl p-1 shadow-sm border border-slate-200 grid grid-cols-3 gap-1 w-full">
              <button
                type="button"
                onClick={() => setDashboardTab("orders")}
                className={`
                  inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-1 py-2.5 md:px-3 md:py-4 rounded-xl font-bold text-[11px] sm:text-sm md:text-xl transition-all duration-300 min-w-0
                  ${dashboardTab === "orders"
                    ? "bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/30"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}
                `}
              >
                <ShoppingCart className="w-4 h-4 md:w-[22px] md:h-[22px] shrink-0" />
                <span className="truncate w-full text-center">Orders</span>
              </button>
              <button
                type="button"
                onClick={() => setDashboardTab("printing")}
                className={`
                  inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-1 py-2.5 md:px-3 md:py-4 rounded-xl font-bold text-[11px] sm:text-sm md:text-xl transition-all duration-300 min-w-0
                  ${dashboardTab === "printing"
                    ? "bg-gradient-to-br from-cyan-600 to-blue-600 text-white shadow-md shadow-cyan-500/30"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}
                `}
              >
                <Printer className="w-4 h-4 md:w-[22px] md:h-[22px] shrink-0" />
                <span className="truncate w-full text-center">Printing</span>
              </button>
              <button
                type="button"
                onClick={() => setDashboardTab("production")}
                className={`
                  inline-flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 px-1 py-2.5 md:px-3 md:py-4 rounded-xl font-bold text-[11px] sm:text-sm md:text-xl transition-all duration-300 min-w-0
                  ${dashboardTab === "production"
                    ? "bg-gradient-to-br from-fuchsia-600 to-pink-600 text-white shadow-md shadow-fuchsia-500/30"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"}
                `}
              >
                <PackageCheck size={16} className="md:hidden shrink-0" />
                <PackageCheck size={22} className="hidden md:block" />
                <span className="truncate w-full text-center">Production</span>
              </button>
            </div>

            {dashboardTab === "orders" && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(isAdmin(authState.role) ? orderCardsAdmin : orderCardsStaff)
                  .filter((item) => !item.requiresTickets || canViewTickets(authState.role))
                  .map((item, i) => {
                  const showAmount = canViewFinancials(authState.role);
                  return (
                    <SummaryCard
                      key={i}
                      item={item}
                      onClick={() => navigate(item.path)}
                      options={{
                        showAmount: showAmount && !item.extraLabel && !item.hideSecondaryStats,
                        compact: !isAdmin(authState.role),
                      }}
                    />
                  );
                })}
              </div>
            )}

            {dashboardTab === "printing" && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {printingCards
                  .filter((item) => {
                    if (item.adminOnly && !isAdmin(authState.role)) return false;
                    if (item.requiresReturns && !canViewReturns(authState.role)) return false;
                    if (item.requiresTickets && !canViewTickets(authState.role)) return false;
                    return true;
                  })
                  .map((item, i) => (
                    <SummaryCard
                      key={i}
                      item={item}
                      onClick={() => navigate(item.path)}
                      options={{
                        showAmount: canViewFinancials(authState.role) && !item.extraLabel && !item.hideSecondaryStats,
                        showPieces: !item.extraLabel && !item.hideSecondaryStats,
                        compact: !isAdmin(authState.role),
                      }}
                    />
                  ))}
              </div>
            )}

            {dashboardTab === "production" && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {printletCards
                    .filter((item) => {
                      if (item.adminOnly && !isAdmin(authState.role)) return false;
                      if (item.requiresTickets && !canViewTickets(authState.role)) return false;
                      return true;
                    })
                    .map((item, i) => (
                      <SummaryCard
                        key={i}
                        item={item}
                        onClick={() => navigate(item.path)}
                        options={{
                          showAmount: canViewFinancials(authState.role) && !item.extraLabel && !item.hideSecondaryStats,
                          compact: !isAdmin(authState.role),
                        }}
                      />
                    ))}
                  {isAdmin(authState.role) && (
                    <button
                      onClick={() => navigate("/printlet")}
                      className="group relative overflow-hidden text-right text-white bg-gradient-to-br from-fuchsia-600 to-pink-600 rounded-xl p-3.5 shadow-md hover:shadow-xl hover:scale-[1.02] active:scale-[0.985] transition-all duration-300 border border-white/20 flex flex-col justify-between min-h-[108px]"
                    >
                      <h3 className="font-bold text-base md:text-lg">لوحة Printlet</h3>
                      <p className="text-sm text-pink-100 mt-2">طباعة خارجية · تتبع · مالية</p>
                      <div className="mt-3 inline-flex items-center gap-1 text-sm font-semibold">
                        فتح اللوحة
                        <ArrowUpRight size={14} />
                      </div>
                    </button>
                  )}
                </div>

                {/* {isAdmin(authState.role) && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    {isLoadingPrintletDash
                      ? Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="bg-white border border-slate-200 rounded-xl h-16 animate-pulse" />
                        ))
                      : printletStats.map((stat) => (
                          <div key={stat.label} className="bg-white border border-slate-200 rounded-xl px-3 py-2.5">
                            <p className="text-xs text-slate-500">{stat.label}</p>
                            <p className="text-lg font-extrabold text-slate-800 leading-tight">{stat.value}</p>
                          </div>
                        ))}
                  </div>
                )} */}
              </div>
            )}
          </div>
        )}

        {/* ═══════════ ADMIN TOOLS — Big feature buttons ═══════════ */}
        {(isAdmin(authState.role) || canViewAnalytics(authState.role)) && (
          <section className={`grid gap-4 ${canViewAnalytics(authState.role) ? "md:grid-cols-2" : ""}`}>
            {isAdmin(authState.role) && (
            <button
              onClick={() => navigate("/production")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-indigo-600 to-blue-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-indigo-500/20 hover:shadow-2xl hover:shadow-indigo-500/30 hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform" />
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 backdrop-blur-md text-white shrink-0 group-hover:scale-110 transition-transform">
                  <Printer size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">لوحة الإنتاج</p>
                  <p className="text-sm text-indigo-100 mt-0.5">طابور الطباعة — ٦×٢ ماكينة</p>
                </div>
              </div>
            </button>
            )}

            {canViewAnalytics(authState.role) && (
            <button
              onClick={() => navigate("/analytics")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-purple-600 to-violet-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-purple-500/20 hover:shadow-2xl hover:shadow-purple-500/30 hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform" />
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 backdrop-blur-md text-white shrink-0 group-hover:scale-110 transition-transform">
                  <BarChart3 size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">لوحة التحليلات</p>
                  <p className="text-sm text-purple-100 mt-0.5">إيرادات · أرباح · واتساب</p>
                </div>
              </div>
            </button>
            )}

            {isOwner(authState.role) && (
            <button
              onClick={() => navigate("/production/analytics")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-sky-600 to-cyan-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-sky-500/20 hover:shadow-2xl hover:shadow-sky-500/30 hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform" />
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 backdrop-blur-md text-white shrink-0 group-hover:scale-110 transition-transform">
                  <Activity size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">تحليل الإنتاج</p>
                  <p className="text-sm text-sky-100 mt-0.5">الباتشات · القطع · سجل الإكمال</p>
                </div>
              </div>
            </button>
            )}

            {isAdmin(authState.role) && (
            <button
              onClick={() => navigate("/customer-analytics")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-rose-600 to-orange-500 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-rose-500/20 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 text-white shrink-0">
                  <Users size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">تحليل العملاء</p>
                  <p className="text-sm text-rose-100 mt-0.5">الدفع المتروك · الكوبونات · الطلبات</p>
                </div>
              </div>
            </button>
            )}

            {canViewAnalytics(authState.role) && (
            <button
              onClick={() => navigate("/fincart-analytics")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-cyan-600 to-teal-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-cyan-500/20 hover:shadow-2xl hover:shadow-cyan-500/30 hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 backdrop-blur-md text-white shrink-0 group-hover:scale-110 transition-transform">
                  <Truck size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">تحليلات Fincart</p>
                  <p className="text-sm text-cyan-100 mt-0.5">تسليم · إرجاع · محافظات · شركات الشحن</p>
                </div>
              </div>
            </button>
            )}

            

            

            {canViewOrders(authState.role) && (
            <button
              onClick={() => navigate("/payments")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-emerald-500/20 hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 text-white shrink-0">
                  <CreditCard size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">المدفوعات</p>
                  <p className="text-sm text-emerald-100 mt-0.5">Kashier · معاملات · مطابقة</p>
                </div>
              </div>
            </button>
            )}

            {/* <button
              onClick={() => navigate("/printlet")}
              className="group relative overflow-hidden text-right bg-gradient-to-br from-fuchsia-600 to-pink-600 rounded-2xl px-4 py-3.5 md:px-5 md:py-4 shadow-lg shadow-fuchsia-500/20 hover:shadow-2xl hover:shadow-fuchsia-500/30 hover:-translate-y-0.5 transition-all duration-300 border border-white/20"
            >
              <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl group-hover:scale-125 transition-transform" />
              <div className="relative flex items-center gap-3">
                <div className="inline-flex p-2.5 rounded-xl bg-white/15 backdrop-blur-md text-white shrink-0 group-hover:scale-110 transition-transform">
                  <PackageCheck size={22} />
                </div>
                <div className="min-w-0">
                  <p className="text-lg md:text-xl font-bold text-white leading-tight">Printlet</p>
                  <p className="text-sm text-pink-100 mt-0.5">طباعة خارجية · تتبع · مالية</p>
                </div>
              </div>
            </button> */}
          </section>
        )}

        {/* ═══════════ SEARCH ═══════════ */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-sm">
              <Search size={18} />
            </div>
            <h3 className="text-xl md:text-3xl font-bold text-slate-800">البحث السريع</h3>
          </div>

          <div className={`grid grid-cols-1 ${isViewer(authState.role) ? "" : "md:grid-cols-2"} gap-4`}>
            <ActionCard
              icon={Search}
              title="البحث عن طلب"
              description="ابحث باستخدام رقم الطلب أو بيانات العميل"
              onClick={() => navigate("/search-orders")}
              accent="indigo"
            />
            
            {!isViewer(authState.role) && (
              <ActionCard
                icon={PackageSearch}
                title="البحث عن تصميم"
                description="ابحث عن التصميمات المضافة للنظام"
                onClick={() => navigate("/search-designs")}
                accent="emerald"
              />
            )}
          </div>
        </section>

        

        {isAdmin(authState.role) && (
          <section className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 text-white shadow-sm">
                <Layers size={18} />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-800">أدوات الإدارة</h3>
                <p className="text-xs text-slate-500">الفئات، العلامات، الكوبونات، الحسابات</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ActionCard
                icon={Tag}
                title="فئات التصاميم"
                description="إدارة فئات المنتجات"
                onClick={() => navigate("/categories")}
                accent="purple"
              />
              <ActionCard
                icon={Layers}
                title="أنواع الهواتف"
                description="إدارة أنواع المنتجات"
                onClick={() => navigate("/brands")}
                accent="teal"
              />
              <ActionCard
                icon={Tag}
                title="أكواد الخصم"
                description="إنشاء وإدارة كوبونات الخصم"
                onClick={() => navigate("/coupons")}
                accent="orange"
              />
              <ActionCard
                icon={Users}
                title="إدارة الحسابات"
                description="إنشاء Owner / Admin / Moderator / Viewer بإيميل وباسوورد"
                onClick={() => navigate("/users")}
                accent="indigo"
              />
              <ActionCard
                  icon={RefreshCw}
                  title="تحديث حالة جماعي"
                  description="لصق أرقام طلبات أو موبايل وتحويلهم لحالة (Printing Hold / Printed / Girabatcom…)"
                  onClick={() => navigate("/bulk-status-update")}
                  accent="indigo"
                  span={1}
                />
              {canViewOrderActivity(authState.role) && (
                <ActionCard
                  icon={ScrollText}
                  title="سجل نشاط الطلبات"
                  description="من غيّر الحالة، ومتى، وإرجاع الحالات المؤهلة"
                  onClick={() => navigate("/admin/order-activity-log")}
                  accent="slate"
                  span={1}
                />
              )}
            </div>
          </section>
        )}

        {/* ═══════════ جه ننفذ؟ (Admin only) ═══════════ */}
        {isAdmin(authState.role) && (
          <section className="space-y-3">
            <SectionHeader
              icon={Zap}
              title="جه ننفذ؟"
              subtitle="متابعة واتساب وتحديث الحالة وتنفيذ الطلبات"
              color="amber"
              isOpen={isExecuteOpen}
              onToggle={() => setIsExecuteOpen(!isExecuteOpen)}
            />
            <div className={`overflow-hidden transition-all duration-500 ${isExecuteOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-1">
                <ActionCard
                  icon={Phone}
                  title="متابعة تأكيد WhatsApp"
                  description="إرسال قالب المتابعة (82964) لآخر طلب لكل رقم"
                  onClick={() => navigate("/follow-up-phones")}
                  accent="emerald"
                  span={2}
                />
                <ActionCard
                  icon={Clock}
                  title="انتظار التأكيد"
                  description="المنتج توفر وجاري التأكيد"
                  onClick={() => navigate("/waiting-confirming-orders")}
                  accent="amber"
                  span={2}
                />
                <ActionCard
                  icon={CheckCircle}
                  title="نفذ الأوردر"
                  description="نفذ الأوردر عند teeplus"
                  onClick={() => navigate("/confirming-orders")}
                  accent="emerald"
                  span={2}
                />
                <ActionCard
                  icon={XCircle}
                  title="هيلغي"
                  description="الغي الأوردر عند teeplus"
                  onClick={() => navigate("/cancelled-by-customer-orders")}
                  accent="rose"
                  span={2}
                />
                <ActionCard
                  icon={MessageSquare}
                  title="إرسال قوالب WhatsApp"
                  description="إرسال قالب محدد لقائمة أرقام (مثل 83267 بعد الإلغاء)"
                  onClick={() => navigate("/bulk-whatsapp")}
                  accent="emerald"
                  span={2}
                />
                
              </div>
            </div>
          </section>
        )}

        {/* ═══════════ CATALOG (collapsible, Admin only) ═══════════ */}
        {isAdmin(authState.role) && (
          <section className="space-y-3">
            <SectionHeader
              icon={ShoppingBag}
              title="الكتالوج"
              subtitle="المنتجات الجاهزة والتصنيفات"
              color="indigo"
              isOpen={isCatalogOpen}
              onToggle={() => setIsCatalogOpen(!isCatalogOpen)}
            />
            <div className={`overflow-hidden transition-all duration-500 ${isCatalogOpen ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 pt-1">
                <ActionCard
                  icon={Shapes}
                  title="أنواع الفئات"
                  description="التصنيفات الرئيسية للمنتجات الجاهزة"
                  onClick={() => navigate("/admin/catalog/category-types")}
                  accent="indigo"
                />
                <ActionCard
                  icon={Palette}
                  title="الألوان"
                  description="كتالوج الألوان للمتغيرات"
                  onClick={() => navigate("/admin/catalog/colors")}
                  accent="pink"
                />
                <ActionCard
                  icon={Type}
                  title="خطوط التخصيص"
                  description="ارفع خطوط الأسماء واستخدمها في أي تصميم"
                  onClick={() => navigate("/admin/catalog/fonts")}
                  accent="violet"
                />
                <ActionCard
                  icon={Smartphone}
                  title="أنواع الكفر"
                  description="شفاف، حواف سوداء، وغيرها"
                  onClick={() => navigate("/admin/catalog/case-styles")}
                  accent="orange"
                />
                <ActionCard
                  icon={ShoppingBag}
                  title="المنتجات الجاهزة"
                  description="إضافة وتعديل المنتجات والمتغيرات"
                  onClick={() => navigate("/admin/catalog/products")}
                  accent="violet"
                />
              </div>
            </div>
          </section>
        )}
        

        {/* ═══════════ OTHERS (collapsible) ═══════════ */}
        {!isViewer(authState.role) && (
          <section className="space-y-3">
            <SectionHeader
              icon={Archive}
              title="أخرى"
              subtitle="طلبات قديمة، سبام، مرتجعات"
              color="blue"
              isOpen={isOthersOpen}
              onToggle={() => setIsOthersOpen(!isOthersOpen)}
            />
            <div className={`overflow-hidden transition-all duration-500 ${isOthersOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                <ActionCard
                  icon={Archive}
                  title="الطلبات القديمة"
                  description="الطلبات المكتملة سابقاً"
                  onClick={() => navigate("/old-orders")}
                  accent="slate"
                />
                <ActionCard
                  icon={AlertTriangle}
                  title="الطلبات السبام"
                  description="طلبات غير حقيقية"
                  onClick={() => navigate("/spam-orders")}
                  accent="rose"
                />
                <ActionCard
                  icon={RotateCcw}
                  title="المرتجعات"
                  description="مخزون القطع المرتجعة للإنتاج"
                  onClick={() => navigate("/returns")}
                  accent="orange"
                />
              </div>
            </div>
          </section>
        )}

        {/* Viewer: old + spam orders only */}
        {isViewer(authState.role) && (
          <section className="space-y-3">
            <SectionHeader
              icon={Archive}
              title="أخرى"
              subtitle="عرض فقط"
              color="blue"
              isOpen={isOthersOpen}
              onToggle={() => setIsOthersOpen(!isOthersOpen)}
            />
            <div className={`overflow-hidden transition-all duration-500 ${isOthersOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <ActionCard
                  icon={Archive}
                  title="الطلبات القديمة"
                  description="عرض فقط"
                  onClick={() => navigate("/old-orders")}
                  accent="slate"
                />
                <ActionCard
                  icon={AlertTriangle}
                  title="الطلبات السبام"
                  description="عرض فقط"
                  onClick={() => navigate("/spam-orders")}
                  accent="rose"
                />
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="pt-6 pb-2 text-center">
          <p className="text-xs text-slate-400">
            {new Date().getFullYear()} © Girabatcom — لوحة تحكم
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AdminDashboard;